/**
 * WorkforceScheduleTimeSandbox — "My Schedule & Time Off", the LIVE self-service
 * screen (2026-08-01: promoted from working copy, renamed by the operator; the
 * separate My Time screen is retired and its surfaces are the tabs here).
 *
 * ONE rail item combining the day/week/month schedule (sandbox/schedule-view.tsx)
 * and everything My Time carried — time off, open shifts, swaps, preferences,
 * history. Structure: one tab row, Schedule first, the five time surfaces after
 * it; one shared stat band across every tab; the three time actions promoted to
 * page-level buttons so they're reachable wherever you are. The "new schedule
 * published" acknowledgement lives in the shell (shell/ScheduleAckBanner.tsx) so
 * it shows on every rail item.
 *
 * The time-off table, preferences, incoming-swap banner and all four modals are
 * imported from WorkforceMyTime.tsx, which is now a COMPONENT MODULE only — its
 * screen and route are gone (screens.tsx).
 *
 * ── Data (all real ops, nothing fabricated) ──────────────────────────────
 *   getMyDay ×N · getMySchedule                       the schedule (roster) surface
 *   getMyTime · getMyUpcomingShifts                   the time surfaces
 *   claimOpenShift · createSwapRequest · createTimeOffRequest
 *   respondToSwapRequest · cancelTimeOffRequest · updateTimeOffRequest
 *   cancelSwapRequest · updateSwapRequest · reportSick
 *
 * ── Honest gaps (no op exists; not faked) ────────────────────────────────
 *   · "Branch closed" is not a field on any response — a day with no shift
 *     reads "Not scheduled".
 *   · reportSick takes no date, so the selection panel offers it on today only.
 *   · Shift times and breaks are manager-owned; no self-service op can change
 *     them, so those affordances render disabled against REQ-123.
 *
 * 2026-08-01, operator instruction: the Schedule tab is the ROSTER ONLY — no
 * appointments, no walk-ins, no open shifts, no booking. Appointments live in
 * My Day; open shifts keep their own tab here.
 */
import { useState } from "react";
import { Btn, Icon, Pill, T } from "@v0y4ge/jrni-ui";
import { Screen } from "../../../shell/chrome";
import { DataSourceStrip } from "../../../shell/opSource";
import { useNav } from "../../../shell/router";
import { toast } from "../../../shell/toast";
import { EmptyState } from "../../../shell/composites";
import { can } from "../../../shell/access";
import { useWfResource, useBusy } from "../useWorkforce";
import {
  getMyTime, getMyUpcomingShifts, respondToSwapRequest, createSwapRequest, createTimeOffRequest,
  cancelTimeOffRequest, updateTimeOffRequest, cancelSwapRequest, updateSwapRequest, claimOpenShift, reportSick,
  type MyTimeResponse, type IncomingSwap, type TimeOffEntry, type WfRequest,
} from "../wf-api";
import { dayLabel, hmLabel, requestStatusPill, mondayOf } from "../wf-shared";
import {
  TimeOffTab, PreferencesTab, IncomingSwapBanner,
  RequestTimeOffModal, InitiateSwapModal, ReportSickModal, EditSwapModal,
} from "../WorkforceMyTime";
import { ScheduleView, fetchView, Stat, type Mode, type ViewData } from "./schedule-view";
import { useT } from "../../../lib/i18n";

const TABS = [
  { id: "schedule", label: "Schedule", labelKey: "workforce.scheduleTime.tabSchedule" },
  { id: "timeoff", label: "Time off", labelKey: "workforce.myTime.timeOff" },
  { id: "open", label: "Open shifts", labelKey: "workforce.myTime.openShifts" },
  { id: "swaps", label: "Swaps", labelKey: "workforce.myTime.swaps" },
  { id: "prefs", label: "Preferences", labelKey: "workforce.myTime.preferences" },
  { id: "history", label: "History", labelKey: "workforce.myTime.history" },
] as const;
type TabId = typeof TABS[number]["id"];

export default function WorkforceScheduleTimeSandbox() {
  const t = useT();
  const nav = useNav();
  const todayIso = new Date().toISOString().slice(0, 10);
  const paramTab = TABS.find((x) => x.id === nav.params.tab)?.id;
  const [tab, setTab] = useState<TabId>(paramTab ?? "schedule"); // ui-local
  const [mode, setMode] = useState<Mode>(nav.params.mode === "day" || nav.params.mode === "month" ? (nav.params.mode as Mode) : "week"); // ui-local
  const [anchor, setAnchor] = useState(typeof nav.params.date === "string" ? nav.params.date : todayIso); // ui-local

  const { busy, run } = useBusy();
  const canRequest = can("swaps.request");

  const viewKey = mode === "week" ? mondayOf(anchor) : mode === "month" ? anchor.slice(0, 7) : anchor;
  const view = useWfResource<ViewData>(`st:${mode}:${viewKey}`, () => fetchView(mode, anchor));
  const myTime = useWfResource<MyTimeResponse>("st:mytime", () => getMyTime());
  const upcoming = useWfResource("st:upcoming", () => getMyUpcomingShifts());
  const data = myTime.data;

  const [timeOffFrom, setTimeOffFrom] = useState<string | null>(null); // ui-local
  const [swapOpen, setSwapOpen] = useState(false); // ui-local
  const [sickOpen, setSickOpen] = useState(false); // ui-local
  const [editTimeOff, setEditTimeOff] = useState<TimeOffEntry | null>(null); // ui-local
  const [editSwap, setEditSwap] = useState<WfRequest | null>(null); // ui-local
  const [skillFilter, setSkillFilter] = useState<"all" | "mine">("all"); // ui-local
  const [respondedSwaps, setRespondedSwaps] = useState<Record<string, true>>({}); // ui-local — hides a banner until the reloaded record reflects the reply

  const days = view.data?.days || [];
  const dates = view.data?.dates || [];
  const sched = view.data?.sched || null;
  const hoursPct = sched?.hours && sched.hours.target > 0 ? Math.round((sched.hours.scheduled / sched.hours.target) * 100) : 0;

  const reloadAll = () => { view.reload(); myTime.reload(); };

  const respondSwap = (swap: IncomingSwap, decision: "accept" | "decline") => void run(`swap-${swap.id}`, async () => {
    try {
      await respondToSwapRequest(swap.id, decision);
      setRespondedSwaps((m) => ({ ...m, [swap.id]: true }));
      toast({ tone: decision === "accept" ? "success" : "info", title: decision === "accept" ? t("workforce.scheduleTime.swapAccepted", "Swap accepted") : t("workforce.scheduleTime.swapDeclined", "Swap declined"), subtitle: swap.fromName });
      myTime.reload();
    } catch (e) { toast({ tone: "danger", title: t("workforce.myTime.couldnTRespond", "Couldn't respond"), subtitle: e instanceof Error ? e.message : String(e) }); }
  });
  const claim = (shiftId: string, label: string) => void run(`claim:${shiftId}`, async () => {
    try { await claimOpenShift(shiftId); toast({ tone: "success", title: t("workforce.myTime.shiftClaimed", "Shift claimed"), subtitle: label }); reloadAll(); }
    catch (e) { toast({ tone: "danger", title: t("workforce.myTime.couldnTClaimThatShift", "Couldn't claim that shift"), subtitle: e instanceof Error ? e.message : String(e) }); }
  });
  const cancelTimeOff = (e: TimeOffEntry) => void run(`cxto:${e.id}`, async () => {
    try { await cancelTimeOffRequest(e.id); toast({ tone: "info", title: t("workforce.myTime.requestCancelled", "Request cancelled") }); reloadAll(); }
    catch (err) { toast({ tone: "danger", title: t("workforce.myTime.couldnTCancel", "Couldn't cancel"), subtitle: err instanceof Error ? err.message : String(err) }); }
  });
  const cancelSwap = (r: WfRequest) => void run(`cxsw:${r.id}`, async () => {
    try { await cancelSwapRequest(r.id); toast({ tone: "info", title: t("workforce.myTime.swapCancelled", "Swap cancelled") }); reloadAll(); }
    catch (err) { toast({ tone: "danger", title: t("workforce.myTime.couldnTCancel", "Couldn't cancel"), subtitle: err instanceof Error ? err.message : String(err) }); }
  });

  const openShiftRows = (data?.openShifts || []).filter((s) => (skillFilter === "mine" ? s.matchesMySkills !== false : true));
  const swapRows = (data?.myRequests || []).filter((r) => r.kind !== "timeoff");
  const badge: Record<TabId, number> = {
    schedule: 0,
    timeoff: data?.timeOffEntries.length ?? 0,
    open: data?.openShifts.length ?? 0,
    swaps: (data?.incomingSwaps.length ?? 0) + swapRows.length,
    prefs: 0,
    history: 0,
  };
  const card: React.CSSProperties = { border: `1px solid ${T.border}`, borderRadius: 12, background: T.white, overflow: "hidden" };
  const rowBtn: React.CSSProperties = { border: `1px solid ${T.n5}`, background: T.white, borderRadius: 6, padding: "4px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: T.font, color: T.n2 };

  return (
    <Screen rail="wf-mysched">
      <DataSourceStrip screen="wfscheduletime-sandbox" reqId="REQ-036"
        ops={["getMyDay", "getMySchedule", "getMyTime", "getMyUpcomingShifts", "claimOpenShift", "createSwapRequest", "createTimeOffRequest", "respondToSwapRequest", "cancelTimeOffRequest", "updateTimeOffRequest", "cancelSwapRequest", "updateSwapRequest", "reportSick"]} />
      <div style={{ padding: "18px 24px 28px", fontFamily: T.font, display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: T.n1 }}>{t("workforce.scheduleTime.titleWithTimeOff", "My Schedule & Time Off")}</h1>
          </div>
          <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <Btn variant="secondary" size="sm" onClick={() => setTimeOffFrom(anchor)}><Icon.calendar size={12} /> {t("workforce.myTime.requestTimeOff", "Request time off")}</Btn>
            {canRequest ? <Btn variant="secondary" size="sm" onClick={() => setSwapOpen(true)}><Icon.refresh size={12} /> {t("workforce.myTime.initiateShiftSwap", "Initiate shift swap")}</Btn> : null}
            <Btn variant="secondary" size="sm" style={{ color: T.red1 }} onClick={() => setSickOpen(true)}><Icon.warn size={12} /> {t("workforce.myTime.reportSick", "Report sick")}</Btn>
          </div>
        </div>

        <div style={{ display: "flex", gap: 28, alignItems: "flex-start", flexWrap: "wrap", padding: "13px 16px", border: `1px solid ${T.border}`, borderRadius: 12, background: T.white, boxShadow: T.shadowSm }}>
          {sched?.hours ? <Stat label={t("workforce.mySchedule.hours", "Hours")} value={`${sched.hours.scheduled}h`} sub={`${t("workforce.mySchedule.target", "target")} ${sched.hours.target}h`} bar={{ pct: hoursPct, tone: hoursPct > 100 ? T.orange1 : T.p1 }} /> : null}
          {data ? <Stat label={t("workforce.myTime.ptoBalance", "PTO balance")} value={`${data.kpis.ptoBalanceDays}d`} sub={t("workforce.scheduleTime.remaining", "remaining")} /> : null}
          {data ? <Stat label={t("workforce.myTime.pendingRequests", "Pending requests")} value={String(data.kpis.pendingRequests)} sub={data.kpis.awaitingMyReply ? `${data.kpis.awaitingMyReply} ${t("workforce.scheduleTime.awaitingYou", "awaiting you")}` : undefined} /> : null}
          {data ? <Stat label={t("workforce.myTime.openShiftsAvailable", "Open shifts")} value={String(data.kpis.openShiftsAvailable)} sub={t("workforce.mySchedule.claimable", "claimable")} /> : null}
          {data ? <Stat label={t("workforce.myTime.premiumEarnedYtd", "Premium YTD")} value={`$${data.kpis.premiumEarnedYtd}`} /> : null}
        </div>

        {(data?.incomingSwaps || []).filter((s) => !respondedSwaps[s.id]).map((s) => (
          <IncomingSwapBanner key={s.id} swap={s} busy={busy} onRespond={(d) => respondSwap(s, d)} />
        ))}

        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {TABS.map((item) => (
            <button key={item.id} data-testid={`st-tab-${item.id}`} onClick={() => setTab(item.id)}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 8, border: "none", background: tab === item.id ? T.p1 : T.white, color: tab === item.id ? T.white : T.n2, fontSize: 13, fontWeight: tab === item.id ? 700 : 500, cursor: "pointer", fontFamily: T.font, boxShadow: tab === item.id ? "none" : T.shadowChrome }}>
              {t(item.labelKey, item.label)}
              {badge[item.id] > 0 ? <span style={{ padding: "0 6px", height: 18, minWidth: 18, borderRadius: 9, background: tab === item.id ? "rgba(255,255,255,0.25)" : T.p3, color: tab === item.id ? T.white : T.p1, fontSize: 10.5, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{badge[item.id]}</span> : null}
            </button>
          ))}
        </div>

        {tab === "schedule" ? (
          <ScheduleView mode={mode} setMode={setMode} anchor={anchor} setAnchor={setAnchor}
            dates={dates} days={days} roster={view.data?.roster || {}} sched={sched} myTime={data}
            loading={view.loading && !view.data} error={view.error}
            busy={busy} run={run} todayIso={todayIso} onReload={reloadAll}
            onRequestTimeOff={(iso) => setTimeOffFrom(iso)} onReportSick={() => setSickOpen(true)} />
        ) : myTime.error ? (
          <div style={{ ...card, padding: 40, textAlign: "center", color: T.soft, fontSize: 13 }}>{t("workforce.myTime.couldnTLoadMyTime", "Couldn't load My Time:")} {myTime.error}</div>
        ) : !data ? (
          <div style={{ ...card, padding: 40, textAlign: "center", color: T.soft, fontSize: 13 }}>{t("common.status.loading", "Loading…")}</div>
        ) : tab === "timeoff" ? (
          <TimeOffTab data={data} busy={busy} onEdit={setEditTimeOff} onCancel={cancelTimeOff} />
        ) : tab === "open" ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "flex", gap: 6 }}>
              {(["all", "mine"] as const).map((f) => (
                <button key={f} onClick={() => setSkillFilter(f)} style={{ padding: "5px 14px", borderRadius: 999, border: `1px solid ${skillFilter === f ? T.p1 : T.n5}`, background: skillFilter === f ? T.p3 : T.white, color: skillFilter === f ? T.p1 : T.n2, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: T.font }}>
                  {f === "all" ? t("workforce.myTime.allOpenShifts", "All open shifts") : t("workforce.myTime.matchMySkills", "Match my skills")}
                </button>
              ))}
            </div>
            <div style={card}>
              {openShiftRows.length === 0 ? <EmptyState icon={<Icon.plus size={18} />} title={t("workforce.myTime.noOpenShifts", "No open shifts")} body={t("workforce.myTime.unassignedShiftsAtYourLocationShowUpHere", "Unassigned shifts at your location show up here.")} /> : openShiftRows.map((s) => (
                <div key={s.id} data-testid="st-openshift-row" style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 20px", borderBottom: `1px solid ${T.n6}` }}>
                  <span style={{ flex: 1 }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: T.n1 }}>{dayLabel(s.date, "md")} · {hmLabel(s.start)}–{hmLabel(s.end)}{s.premiumPct ? <Pill tone="warn">+{s.premiumPct}%</Pill> : null}</span>
                    <span style={{ display: "block", fontSize: 11.5, color: T.soft, marginTop: 2 }}>{s.reason ?? t("workforce.scheduleTime.coverageNeeded", "Coverage needed")}{s.postedBy ? ` · ${t("workforce.scheduleTime.postedBy", "posted by")} ${s.postedBy}` : ""}</span>
                  </span>
                  {canRequest ? <Btn size="sm" disabled={busy !== null} onClick={() => claim(s.id, `${dayLabel(s.date, "md")} ${hmLabel(s.start)}–${hmLabel(s.end)}`)}>{busy === `claim:${s.id}` ? t("workforce.workforceMyTime.claiming", "Claiming…") : t("workforce.workforceMyTime.claim", "Claim")}</Btn> : null}
                </div>
              ))}
            </div>
          </div>
        ) : tab === "swaps" ? (
          <div style={card}>
            {swapRows.length === 0 ? <EmptyState icon={<Icon.refresh size={18} />} title={t("workforce.myTime.noSwapsYet", "No swaps yet")} body={t("workforce.myTime.swapsYouRequestOrReceiveAppearHere", "Swaps you request or receive appear here.")} /> : swapRows.map((r) => (
              <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 20px", borderBottom: `1px solid ${T.n6}` }}>
                <span style={{ flex: 1, fontSize: 13, color: T.n1 }}>{t("workforce.myTime.swap", "Swap ·")} {dayLabel(r.date, "md")}</span>
                {requestStatusPill(r.status)}
                {r.status === "pending" ? (
                  <span style={{ display: "flex", gap: 6 }}>
                    <button onClick={() => setEditSwap(r)} style={rowBtn}>{t("common.actions.edit", "Edit")}</button>
                    <button onClick={() => cancelSwap(r)} disabled={busy !== null} style={{ ...rowBtn, color: T.red1 }}>{busy === `cxsw:${r.id}` ? "…" : t("common.actions.cancel", "Cancel")}</button>
                  </span>
                ) : null}
              </div>
            ))}
          </div>
        ) : tab === "prefs" ? (
          <PreferencesTab />
        ) : (
          <div style={{ ...card, padding: "6px 22px" }}>
            {(data.history || []).length === 0 ? <EmptyState icon={<Icon.clock size={18} />} title={t("workforce.scheduleTime.noHistory", "Nothing yet")} body={t("workforce.scheduleTime.noHistoryBody", "Approvals, claims and swap replies show up here.")} /> : (data.history || []).map((h, i) => (
              <div key={i} style={{ display: "flex", gap: 10, fontSize: 12.5, color: T.n2, padding: "12px 0", borderTop: i === 0 ? "none" : `1px solid ${T.n6}` }}>
                <span style={{ color: T.soft, fontFamily: T.fontMono, flexShrink: 0 }}>{dayLabel(h.ts, "md")}</span>
                <span><b style={{ color: T.n1 }}>{h.event}</b> — {h.detail}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {timeOffFrom ? (
        <RequestTimeOffModal ptoBalance={data?.kpis.ptoBalanceDays ?? 0} busy={busy === "timeoff"} onClose={() => setTimeOffFrom(null)}
          initial={{ from: timeOffFrom, to: timeOffFrom }}
          onSubmit={(i) => { void run("timeoff", async () => {
            try { await createTimeOffRequest({ ...i, allDay: true }); toast({ tone: "success", title: t("workforce.myTime.timeOffRequested", "Time off requested"), subtitle: dayLabel(i.startDate, "md") }); setTimeOffFrom(null); reloadAll(); }
            catch (e) { toast({ tone: "danger", title: t("workforce.myTime.couldnTSubmitTheRequest", "Couldn't submit the request"), subtitle: e instanceof Error ? e.message : String(e) }); }
          }); }} />
      ) : null}
      {swapOpen ? (
        <InitiateSwapModal shifts={upcoming.data?.shifts ?? []} busy={busy === "swap"} onClose={() => setSwapOpen(false)}
          onSubmit={(i) => { void run("swap", async () => {
            try { await createSwapRequest({ shiftId: i.shiftId, route: i.route }); toast({ tone: "success", title: i.route === "marketplace" ? t("workforce.scheduleTime.postedToMarketplace", "Posted to marketplace") : t("workforce.scheduleTime.swapSent", "Swap request sent"), subtitle: t("workforce.myTime.yourManagerWillReviewIt", "Your manager will review it.") }); setSwapOpen(false); reloadAll(); }
            catch (e) { toast({ tone: "danger", title: t("workforce.myTime.couldnTSubmitTheSwap", "Couldn't submit the swap"), subtitle: e instanceof Error ? e.message : String(e) }); }
          }); }} />
      ) : null}
      {sickOpen ? (
        <ReportSickModal appointmentsToday={upcoming.data?.shifts.find((s) => s.date === todayIso)?.apptCount ?? 0} busy={busy === "sick"} onClose={() => setSickOpen(false)}
          onSubmit={(i) => { void run("sick", async () => {
            try { const r = await reportSick(i); toast({ tone: "success", title: t("workforce.scheduleTime.sickReported", "Sick day reported"), subtitle: `${r.flaggedAppointments} ${t("workforce.scheduleTime.flagged", "appointments flagged for the branch")}` }); setSickOpen(false); reloadAll(); }
            catch (e) { toast({ tone: "danger", title: t("workforce.scheduleTime.couldntReportSick", "Couldn't report sick"), subtitle: e instanceof Error ? e.message : String(e) }); }
          }); }} />
      ) : null}
      {editTimeOff ? (
        <RequestTimeOffModal editing ptoBalance={data?.kpis.ptoBalanceDays ?? 0} initial={{ from: editTimeOff.startDate, to: editTimeOff.endDate, note: editTimeOff.reason }} busy={busy === "edittimeoff"} onClose={() => setEditTimeOff(null)}
          onSubmit={(i) => { const eo = editTimeOff; void run("edittimeoff", async () => {
            try { await updateTimeOffRequest(eo.id, { ...i, allDay: true }); toast({ tone: "success", title: t("workforce.myTime.requestUpdated", "Request updated"), subtitle: dayLabel(i.startDate, "md") }); setEditTimeOff(null); reloadAll(); }
            catch (e) { toast({ tone: "danger", title: t("workforce.myTime.couldnTUpdateRequest", "Couldn't update the request"), subtitle: e instanceof Error ? e.message : String(e) }); }
          }); }} />
      ) : null}
      {editSwap ? (
        <EditSwapModal busy={busy === "editswap"} onClose={() => setEditSwap(null)}
          onSubmit={(i) => { const sw = editSwap; void run("editswap", async () => {
            try { await updateSwapRequest(sw.id, i); toast({ tone: "success", title: t("workforce.myTime.swapUpdated", "Swap updated") }); setEditSwap(null); reloadAll(); }
            catch (e) { toast({ tone: "danger", title: t("workforce.myTime.couldnTUpdateSwap", "Couldn't update the swap"), subtitle: e instanceof Error ? e.message : String(e) }); }
          }); }} />
      ) : null}
    </Screen>
  );
}
